import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import AppIcon from './AppIcon';
import { cn } from '../utils/cn';

type PaginationProps = {
    currentPage: number;
    totalPages: number;
    onPageChange: (newPage: number) => void;
    showEdges?: boolean;
    variant?: 'full' | 'compact';
    hasPreviousPage?: boolean;
    hasNextPage?: boolean;
    pageSize?: number;
    pageSizeOptions?: number[];
    onPageSizeChange?: (size: number) => void;
    totalCount?: number;
};

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    showEdges = true,
    variant = 'full',
    hasPreviousPage,
    hasNextPage,
    pageSize,
    pageSizeOptions = [10, 25, 50],
    onPageSizeChange,
    totalCount,
}) => {
    const { t } = useTranslation();
    const handlePageChange = (newPage: number) => {
        const page = clamp(newPage, 1, totalPages);
        if (page !== currentPage) onPageChange(page);
    };

    const isPrevDisabled = hasPreviousPage !== undefined ? !hasPreviousPage : currentPage === 1;
    const isNextDisabled = hasNextPage !== undefined ? !hasNextPage : currentPage === totalPages;
    const showNavigation = totalPages > 1;

    const getPageNumbers = (): (number | 'ellipsis')[] => {
        const pages: (number | 'ellipsis')[] = [];
        const show = 5;
        const half = Math.floor(show / 2);
        let start = Math.max(1, currentPage - half);
        const end = Math.min(totalPages, start + show - 1);
        start = Math.max(1, Math.min(start, end - show + 1));
        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('ellipsis');
        }
        for (let p = start; p <= end; p++) pages.push(p);
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push('ellipsis');
            pages.push(totalPages);
        }
        return pages;
    };

    // When there's no page-size selector, hide entirely for single-page results
    if (!showNavigation && !onPageSizeChange) return null;

    return (
        <div className="flex items-center justify-between mt-4" role="navigation" aria-label="pagination">
            <div className="flex items-center gap-3">
                {onPageSizeChange && pageSize != null && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{t('pagination.showPerPage')}</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="px-2 py-1 border border-border rounded text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        >
                            {pageSizeOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}
                {totalCount != null && (
                    <span className="text-sm text-muted-foreground">
                        {t('pagination.totalItems', { count: totalCount })}
                    </span>
                )}
            </div>
            {showNavigation && (
                <div className="flex items-center gap-1">
                    {variant === 'full' && showEdges && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            disabled={isPrevDisabled}
                            aria-label={t('pagination.firstPage')}
                        >
                            <AppIcon name="ChevronsLeft" size={16} />
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={isPrevDisabled}
                        aria-label={t('pagination.previousPage')}
                    >
                        <AppIcon name="ChevronLeft" size={16} />
                    </Button>
                    {variant === 'full' && getPageNumbers().map((p, idx) =>
                        p === 'ellipsis' ? (
                            <span
                                key={`e-${idx}`}
                                className="inline-flex items-center justify-center h-9 w-9 text-sm text-muted-foreground"
                            >
                                &hellip;
                            </span>
                        ) : (
                            <Button
                                key={p}
                                variant={p === currentPage ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handlePageChange(p)}
                                className={cn('min-w-[36px]')}
                                aria-current={p === currentPage ? 'page' : undefined}
                            >
                                {p}
                            </Button>
                        )
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={isNextDisabled}
                        aria-label={t('pagination.nextPage')}
                    >
                        <AppIcon name="ChevronRight" size={16} />
                    </Button>
                    {variant === 'full' && showEdges && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={isNextDisabled}
                            aria-label={t('pagination.lastPage')}
                        >
                            <AppIcon name="ChevronsRight" size={16} />
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Pagination;
